import { Button, Checkbox, FormGroup, InlineNotification, SkeletonText, Stack } from '@carbon/react';
import { asProblem, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

const CHANGES = ['password', 'profile', 'addresses'] as const;
const settingsKey = ['ho', 'customer-security'] as const;

/**
 * Which changes a website or app customer confirms with a one-time code sent to their phone (ADR 0123): a new password, profile
 * details, addresses. A customer who just signed in with a code isn't asked again. Configuration per company, audited; it reaches the
 * website within about 15 minutes.
 */
export function CustomerSecurityPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.online.customer_security.manage');
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/customer-security');
      if (!data) throw error;
      return data;
    },
  });
  const [chosen, setChosen] = useState<string[]>();
  const saved = current.data;
  const value = chosen ?? [...(saved?.codeRequiredFor ?? [])];

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/identity/customer-security', {
        body: { codeRequiredFor: value, version: saved?.version ?? 0 },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setChosen(undefined);
      queryClient.setQueryData(settingsKey, next);
    },
  });

  if (current.isLoading) return <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />;
  if (!saved)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(current.error))}
      />
    );

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('customerSecurity.title')} description={t('customerSecurity.hint')} />
      <Stack gap={6}>
        <FormGroup legendText={t('customerSecurity.codeFor')}>
          {CHANGES.map((change) => (
            <Checkbox
              key={change}
              id={`code-for-${change}`}
              labelText={t(`customerSecurity.changes.${change}`)}
              checked={value.includes(change)}
              disabled={!canManage}
              onChange={(_event, { checked }) => {
                save.reset();
                setChosen(checked ? [...value, change] : value.filter((c) => c !== change));
              }}
            />
          ))}
        </FormGroup>
        {saved.isDefault && chosen === undefined && (
          <InlineNotification kind="info" lowContrast hideCloseButton title={t('customerSecurity.default')} />
        )}
        {canManage && (
          <div>
            <Button disabled={chosen === undefined || save.isPending} onClick={() => save.mutate()}>
              {t('admin.save')}
            </Button>
          </div>
        )}
        {save.error && (
          <InlineNotification
            kind="error"
            role="alert"
            lowContrast
            hideCloseButton
            title={problemMessage(t, asProblem(save.error))}
          />
        )}
        {save.isSuccess && (
          <InlineNotification
            kind="success"
            role="status"
            lowContrast
            hideCloseButton
            title={t('customerSecurity.saved')}
          />
        )}
      </Stack>
    </section>
  );
}
